// PASTE #235: out-of-state fulfillment arrangement captured at signup.
// Portal-side metadata only - it NEVER alters the fee resolver (out-of-
// state stays $0 Doral-pickup fallback); it tells the team how to hand
// the client their boxes. Stored on the SignupRow at signup (no
// qbCustomerId yet), then copied onto the ClientDeliveryRow when the QB
// customer is linked. No QuickBooks write (WA #16).

export type FulfillmentChoice = "trucker" | "pickup" | "carrier" | "help-me";

// The FULL set - kept so EXISTING stored records (carrier / help-me) still
// validate + load. normalizeFulfillment stays permissive for backward compat.
export const FULFILLMENT_CHOICES: readonly FulfillmentChoice[] = [
  "trucker",
  "pickup",
  "carrier",
  "help-me",
] as const;

// #434: the two choices that are SELECTABLE going forward, under "Use my own
// carrier". "carrier" (own-account, duplicated the priced Door option) and
// "help-me" (clients without a carrier now use Door/Airport) were removed from
// the picker - existing records on those values are preserved, just no longer
// offered. One shared source so the signup + account editors never drift, EN+ES.
export type SelectableFulfillmentChoice = "trucker" | "pickup";
export const SELECTABLE_FULFILLMENT_CHOICES: readonly SelectableFulfillmentChoice[] =
  ["trucker", "pickup"] as const;

export function fulfillmentOptions(
  locale: "en" | "es" = "en",
): { value: SelectableFulfillmentChoice; title: string; help: string }[] {
  if (locale === "es") {
    return [
      {
        value: "trucker",
        title: "Entregar a mi empresa de transporte",
        help: "Dejamos tus cajas en las instalaciones de tu empresa de transporte en el área de Doral.",
      },
      {
        value: "pickup",
        title: "Recogeré en su punto de recogida de Doral",
        help: "Lun–Dom 7am–2pm EST.",
      },
    ];
  }
  return [
    {
      value: "trucker",
      title: "Deliver to my trucking company",
      help: "We drop your boxes at your trucking company's facility in the Doral area.",
    },
    {
      value: "pickup",
      title: "I'll pick up at your Doral pickup location",
      help: "Mon–Sun 7am–2pm EST.",
    },
  ];
}

export type OutOfStateFulfillment = {
  choice: FulfillmentChoice;
  // choice === "trucker": both required
  truckerName?: string;
  truckerContact?: string; // phone or email
  // choice === "carrier"
  carrierName?: string;
  carrierAccount?: string; // optional when sendLabels is true
  sendLabels?: boolean; // "I'll send you the shipping labels"
  // any choice: optional free note (esp. "help-me")
  note?: string;
};

export function needsFulfillmentHelp(
  f?: OutOfStateFulfillment | null,
): boolean {
  return f?.choice === "help-me";
}

// A short, locale-agnostic read-only summary for checkout / order /
// admin surfaces. Returns null when nothing was captured (e.g. a
// Florida client, who never sees the step).
// #81 i18n (Jul 14): bilingual. Default "en" keeps every existing caller
// (admin surfaces, owner email) byte-identical.
export function fulfillmentSummary(
  f?: OutOfStateFulfillment | null,
  locale: "en" | "es" = "en",
): string | null {
  if (!f) return null;
  const es = locale === "es";
  switch (f.choice) {
    case "trucker":
      return es
        ? `Entrega a empresa de transporte${f.truckerName ? ` - ${f.truckerName}` : ""}`
        : `Deliver to trucking company${f.truckerName ? ` - ${f.truckerName}` : ""}`;
    case "pickup":
      return es ? "Recogida en Doral" : "Pickup in Doral";
    case "carrier":
      return (es
        ? `Envío con tu cuenta de ${f.carrierName ? f.carrierName : ""} `
        : `Ship on your ${f.carrierName ? f.carrierName : ""} carrier account`
      ).replace("  ", " ").trim();
    case "help-me":
      return es
        ? "Ayuda de configuración solicitada - el equipo dará seguimiento"
        : "Setup help requested - team follow-up";
    default:
      return null;
  }
}

// The "Fulfillment" line on a client ORDER page. The retired #434 "help-me"
// choice ("Setup help requested - team follow-up") is hidden on a FedEx Door
// or Airport order: that order already has its method, so the old signup
// answer only confuses (Molly ORD-83576E2B). Trucker / Doral pickup lines,
// and every other surface (admin, signup, account), are unchanged.
export function orderFulfillmentLine(
  f: OutOfStateFulfillment | null | undefined,
  orderShippingMethod: string | null | undefined,
  locale: "en" | "es" = "en",
): string | null {
  const method = (orderShippingMethod ?? "").toUpperCase();
  if (f?.choice === "help-me" && (method === "DOOR" || method === "AIRPORT")) return null;
  return fulfillmentSummary(f, locale);
}

function cleanStr(v: unknown, max = 200): string | undefined {
  if (typeof v !== "string") return undefined;
  const t = v.trim();
  return t ? t.slice(0, max) : undefined;
}

// Server-side validate + normalize a raw fulfillment object (from the
// signup POST body or the account edit API). Returns undefined for a
// missing/invalid object, or a null-free normalized value. Enforces the
// per-choice required fields so the stored record is always coherent.
export function normalizeFulfillment(
  raw: unknown,
): OutOfStateFulfillment | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  const choice = r.choice;
  if (
    typeof choice !== "string" ||
    !FULFILLMENT_CHOICES.includes(choice as FulfillmentChoice)
  ) {
    return undefined;
  }
  const c = choice as FulfillmentChoice;
  const note = cleanStr(r.note, 500);

  if (c === "pickup") {
    return { choice: "pickup", ...(note ? { note } : {}) };
  }
  if (c === "help-me") {
    return { choice: "help-me", ...(note ? { note } : {}) };
  }
  if (c === "trucker") {
    const truckerName = cleanStr(r.truckerName);
    const truckerContact = cleanStr(r.truckerContact);
    if (!truckerName || !truckerContact) return undefined; // both required
    return { choice: "trucker", truckerName, truckerContact, ...(note ? { note } : {}) };
  }
  // carrier
  const carrierName = cleanStr(r.carrierName);
  const carrierAccount = cleanStr(r.carrierAccount);
  const sendLabels = r.sendLabels === true;
  if (!carrierName) return undefined; // carrier name required
  if (!sendLabels && !carrierAccount) return undefined; // account required unless sending labels
  return {
    choice: "carrier",
    carrierName,
    ...(carrierAccount ? { carrierAccount } : {}),
    sendLabels,
    ...(note ? { note } : {}),
  };
}
